import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, Input, Label } from '../../shared/components/Form';
import { changePassword } from './api';
import { useAuth } from './AuthContext';

function getRoleHome(me: { isServiceAdmin: boolean; isCompanyAdmin: boolean } | null) {
    if (me?.isServiceAdmin) return '/admin';
    if (me?.isCompanyAdmin) return '/company';
    return '/projects';
}

export default function ChangePasswordPage() {
    const nav = useNavigate();
    const loc = useLocation();
    const { me, applyAuthUser, logout } = useAuth();

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [err, setErr] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const isForced = !!me?.mustChangePassword;
    const isGuestOnlyUser = !!me?.isGuestOnlyUser;
    const redirectTarget = useMemo(() => {
        const redirect = new URLSearchParams(loc.search).get('redirect');
        if (!redirect || redirect === '/change-password') return null;
        return redirect;
    }, [loc.search]);
    const cancelTarget = redirectTarget ?? getRoleHome(me ?? null);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        setErr(null);

        if (newPassword.length < 8) {
            setErr('새 비밀번호는 8자 이상이어야 합니다.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setErr('새 비밀번호 확인이 일치하지 않습니다.');
            return;
        }
        if (currentPassword === newPassword) {
            setErr('새 비밀번호는 현재 비밀번호와 달라야 합니다.');
            return;
        }

        setSaving(true);
        try {
            const updatedMe = await changePassword({ currentPassword, newPassword });
            applyAuthUser(updatedMe);

            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');

            nav(redirectTarget ?? getRoleHome(updatedMe ?? me ?? null), { replace: true });
        } catch (e: any) {
            const serverMsg = e?.response?.data?.error?.message ?? e?.response?.data?.message ?? e?.message;
            setErr(
                serverMsg === 'InvalidCurrentPassword'
                    ? '현재 비밀번호가 올바르지 않습니다.'
                    : serverMsg === 'NewPasswordMustDiffer'
                      ? '새 비밀번호는 현재 비밀번호와 달라야 합니다.'
                      : serverMsg === 'GuestPasswordChangeForbidden'
                        ? '게스트 전용 계정은 비밀번호를 변경할 수 없습니다.'
                      : serverMsg || '비밀번호 변경 실패',
            );
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="min-h-screen grid place-items-center bg-gradient-to-b from-orange-50 to-sky-50 px-4">
            <Card>
                <div className="mb-4 space-y-1">
                    <h1 className="text-xl font-semibold">{isForced ? '초기 비밀번호 변경' : '비밀번호 변경'}</h1>
                    <p className="text-sm text-gray-600">
                        {isGuestOnlyUser
                            ? '게스트 전용 계정은 프로젝트 접근용으로 발급되며 비밀번호를 변경할 수 없습니다.'
                            : isForced
                            ? '초기 비밀번호로 로그인했습니다. 서비스를 계속 사용하려면 비밀번호를 먼저 변경해야 합니다.'
                            : '현재 비밀번호를 확인한 뒤 새 비밀번호로 변경합니다.'}
                    </p>
                </div>

                {isGuestOnlyUser ? (
                    <div className="w-80 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        계정 관리자에게 문의해 비밀번호를 재발급받아 주세요.
                    </div>
                ) : (
                    <form onSubmit={onSubmit} className="space-y-4 w-80">
                        <div>
                            <Label>현재 비밀번호</Label>
                            <Input
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                type="password"
                                autoComplete="current-password"
                            />
                        </div>
                        <div>
                            <Label>새 비밀번호</Label>
                            <Input
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                type="password"
                                autoComplete="new-password"
                            />
                        </div>
                        <div>
                            <Label>새 비밀번호 확인</Label>
                            <Input
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                type="password"
                                autoComplete="new-password"
                            />
                        </div>
                        {err && <div className="text-sm text-red-600">{err}</div>}
                        <div className="flex items-center gap-2">
                            {!isForced && (
                                <button
                                    type="button"
                                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
                                    onClick={() => nav(cancelTarget, { replace: true })}
                                    disabled={saving}
                                >
                                    취소
                                </button>
                            )}
                            <Button type="submit" disabled={saving}>
                                {saving ? '변경 중…' : isForced ? '비밀번호 변경 후 계속' : '비밀번호 변경'}
                            </Button>
                        </div>
                    </form>
                )}

                <div className="mt-4 flex justify-end">
                    <button
                        type="button"
                        className="text-sm text-gray-600 hover:text-gray-900"
                        onClick={() => {
                            logout();
                            nav('/login?logged_out=1', { replace: true });
                        }}
                    >
                        로그아웃
                    </button>
                </div>
            </Card>
        </div>
    );
}
